import { Head } from "@inertiajs/react"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Reports",
    href: "/reports",
  },
]

interface Report {
  client_id: string
  contract_id: string
  symbol: string
  purchase_date: string
  quantity: number
  original_cost: number
  inflation_adjusted: number
  current_value: number
}

interface ReportsProps {
  mockReports: Report[]
}

export default function Reports({ mockReports }: ReportsProps) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Reports" />

      <div className="flex flex-col gap-5 p-4">
        <h1 className="text-3xl font-bold tracking-tight">Tax Basis Reports</h1>

        <Card>
          <CardHeader>
            <CardTitle>Inflation-Adjusted Stock Reports</CardTitle>
            <CardDescription>
              Stock holdings with inflation-adjusted cost basis
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mockReports.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client ID</TableHead>
                    <TableHead>Contract ID</TableHead>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Purchase Date</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead className="text-right">Original Cost</TableHead>
                    <TableHead className="text-right">Inflation Adjusted</TableHead>
                    <TableHead className="text-right">Current Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockReports.map((report, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{report.client_id}</TableCell>
                      <TableCell>{report.contract_id}</TableCell>
                      <TableCell>{report.symbol}</TableCell>
                      <TableCell>{report.purchase_date}</TableCell>
                      <TableCell className="text-right">{report.quantity}</TableCell>
                      <TableCell className="text-right">
                        ${report.original_cost.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        ${report.inflation_adjusted.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        ${report.current_value.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center py-8 text-muted-foreground">
                No stock data available. Add stock entries to generate reports.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Report Summary</CardTitle>
            <CardDescription>
              Aggregated values across all holdings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Original Cost
                </p>
                <p className="text-2xl font-bold">
                  $
                  {mockReports
                    .reduce((sum, r) => sum + r.original_cost, 0)
                    .toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Inflation Adjusted
                </p>
                <p className="text-2xl font-bold">
                  $
                  {mockReports
                    .reduce((sum, r) => sum + r.inflation_adjusted, 0)
                    .toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Current Value
                </p>
                <p className="text-2xl font-bold">
                  $
                  {mockReports
                    .reduce((sum, r) => sum + r.current_value, 0)
                    .toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
